/**
 * Pyxel ランタイム（CDN 版）のバージョン解決。
 *
 * #1004: 既定は 2.9.9（同梱 Pyodide 314.0.4 = Python 3.14 / Emscripten 5.0.3）。
 * 経緯: #746 で 2.9.6 → 2.9.5 に下げていた。pyxel 2.9.6 で同梱 Pyodide が 0.29.3 → 314.x に
 * ジャンプし、iOS ≤18 の Safari で全作品が `RuntimeError: Unreachable code should not be
 * executed` でクラッシュしたため（iOS/iPadOS 26 のクラッシュは版と無関係で、#748 の
 * cross-origin 化で直った）。iOS ≤18 で 314 系が再び落ちるなら `?pyxel=2.9.5` が退避路
 * （0.29.3 世代の最終版）。
 *
 * `?pyxel=<x.y.z>` クエリ（または明示 prop）でバージョンを上書きできる。用途:
 *   - kitao 氏への再現報告（同一サイトで ?pyxel=2.9.6[落ちる] ↔ ?pyxel=2.9.5[動く] を URL 切替で提示）
 *   - 314 世代のどの版が iOS を壊したかの bisection
 *
 * セキュリティ: 上書き値は `^\d+\.\d+\.\d+$` で厳格に検証し、ホスト/リポジトリは
 * ハードコードする。可変なのは kitao/pyxel の**リリース番号のみ**なので、CSP の
 * `script-src` prefix と `<script src>` は常に同一バージョンで一致し、空白/セミコロン等を
 * 注入して CSP を破ることはできない（CSP 境界は不変）。#1004 から版は**配信元の選択**にも
 * 使う（`pyxelRuntimeBase`）が、選べるのはハードコードした 2 つのホストのどちらかだけ。
 * 詳細は doc/architecture/csp.md / doc/architecture/pyxel_runtime.md を参照。
 */

/** クエリ/prop 未指定時に使う既定の Pyxel バージョン（#746 で 2.9.6 → 2.9.5、#1004 で 2.9.9）。 */
export const DEFAULT_PYXEL_VERSION = '2.9.9'

/**
 * Pyxel WASM が内部でロードする Pyodide の配信元（CSP の script-src / connect-src に prefix で許可）。
 *
 * #1004: Pyxel 3.0 で Pyodide 本体（pyodide.js / wasm / stdlib / lock）の配信元が
 * `kitao.dev/pyxel/pyodide/` に移った（追加パッケージだけは引き続き jsDelivr）。`?pyxel=` で
 * 2.9.x と 3.x を行き来できるよう、**両方を常に許可する**。Pyodide のバージョンは Pyxel に従属
 * するのでここは版無指定の prefix でよい。
 *
 * ⚠️ `public/pyxel-frame.html` の meta CSP と `cdk/lib/frontend-stack.ts` の sandbox CSP は
 * このモジュールを import できないので直書きしている（kitao.dev 側は下の
 * `PYXEL_RUNTIME_HOSTS` の `https://kitao.dev/pyxel/` で prefix ごと覆う）。揃っているかは
 * gennai/tests/utils/pyxelCspAlignment.test.ts と cdk/test/pyxel-sandbox-csp.test.ts が見張る。
 */
export const PYODIDE_SOURCES = [
  'https://cdn.jsdelivr.net/pyodide/',
  'https://kitao.dev/pyxel/pyodide/',
] as const

/**
 * Pyxel 本体（`pyxel.js` と、それが自分の置き場から相対で読む wheel / css / images /
 * import_hook）の配信元。版に依存しない prefix。
 *
 * #1004: 3.0 から公式の配信先が kitao.dev に替わった（`app2html` も公式ドキュメントも
 * `https://kitao.dev/pyxel/<版>/pyxel.js`）。jsDelivr の `gh/kitao/pyxel@<版>/wasm/` は
 * 2.9.x までの公式経路で、3.0 では案内から消えた（タグを打てば機械的に出るが保証は無い）。
 * kitao.dev には 2.9.9 以降しか無いので、`?pyxel=2.9.5` で戻れるよう両方を残す。
 *
 * 版ごとの使い分けは `pyxelRuntimeBase`。blob 経路（PyxelPlayer / PyxelResourceEditor）は
 * そこで決まった**版つきのディレクトリ**だけを CSP に入れるが、cross-origin フレームの meta
 * CSP と CDK の sandbox CSP は版を知らないので、この 2 つを prefix で許可する。
 */
export const PYXEL_RUNTIME_HOSTS = [
  'https://cdn.jsdelivr.net/gh/kitao/',
  'https://kitao.dev/pyxel/',
] as const

/** `x.y.z` の 3 セグメント semver のみ許可（CDN タグに直接埋め込むため厳格に検証する）。 */
const PYXEL_VERSION_RE = /^\d+\.\d+\.\d+$/

/** 上書き値が安全な Pyxel バージョン文字列（`x.y.z`）かどうか。 */
export function isValidPyxelVersion(value: unknown): value is string {
  return typeof value === 'string' && PYXEL_VERSION_RE.test(value)
}

/**
 * 上書き候補（`route.query.pyxel` 等、string | string[] | null など任意の入力）を検証し、
 * 妥当なら採用、そうでなければ既定にフォールバックして最終バージョンを返す。
 */
export function resolvePyxelVersion(override?: unknown): string {
  return isValidPyxelVersion(override) ? override : DEFAULT_PYXEL_VERSION
}

/**
 * 検証済みバージョンから、`pyxel.js` が置かれたディレクトリの URL（末尾スラッシュ付き）を返す。
 *
 * `<script src>` は `${base}pyxel.js`。`pyxel.js` は wheel / css / images を自分の置き場から
 * 相対で読む（`_scriptDir`。2.9.5 と 3.0 で確認）ので、CSP の prefix もこの base で足りる。
 *
 * - 3.0 以降 … `https://kitao.dev/pyxel/<版>/`（公式。#1004）
 * - 〜2.9.x … `https://cdn.jsdelivr.net/gh/kitao/pyxel@<版>/wasm/`（タグの時点のリポジトリ）
 *
 * ⚠️ 同じ分岐を `public/pyxel-frame.html` に直書きしている（import できない）。
 */
export function pyxelRuntimeBase(version: string): string {
  const major = Number(version.split('.')[0])
  return major >= 3
    ? `https://kitao.dev/pyxel/${version}/`
    : `https://cdn.jsdelivr.net/gh/kitao/pyxel@${version}/wasm/`
}

/**
 * 実行して Python が止まった理由 (#745)。
 *
 * Pyodide は Python の例外を JS の Error として投げてくるので、メッセージの中に
 * traceback がテキストで入っている。センパイの自発ヒントは「NameError なのか
 * AttributeError なのか」「どの名前でつまずいたのか」で言うことを変えるため、
 * ここで最低限だけ取り出す。読み取れなければ空で返す（ヒントが出ないだけ）。
 */
export interface PythonErrorInfo {
  errorType: string
  name: string
  message: string
}

/** traceback の最終行 `NameError: name 'x' is not defined` を拾う。 */
const PYTHON_ERROR_LINE_RE = /^([A-Za-z_][A-Za-z0-9_]*Error):\s*(.+)$/
/** `name 'scoer' is not defined` / `... has no attribute 'recct'` の引用名。 */
const QUOTED_NAME_RE = /'([^']+)'/g

export function parsePythonError(text: string): PythonErrorInfo | null {
  if (!text) return null
  let found: { errorType: string, detail: string } | null = null
  for (const line of text.split('\n')) {
    const m = PYTHON_ERROR_LINE_RE.exec(line.trim())
    // traceback は入れ子になりうるので、最後に出てきたものを採る。
    if (m) found = { errorType: m[1]!, detail: m[2]! }
  }
  if (!found) return null

  // 引用された名前のうち、いちばん後ろ = つまずいた名前（`module 'pyxel' has no
  // attribute 'recct'` なら recct のほう）。
  const quoted = [...found.detail.matchAll(QUOTED_NAME_RE)].map(m => m[1]!)
  return {
    errorType: found.errorType,
    name: quoted.length ? quoted[quoted.length - 1]! : '',
    message: found.detail,
  }
}
